/**
 * Text formatting utilities for Agora composers.
 *
 * Storage format (stored as plain text in DB):
 *   **text**  → <strong>
 *   *text*    → <em>
 *   __text__  → <u>
 */

import React from 'react'

function isWordChar(ch) {
  return /[A-Za-z0-9_]/.test(ch || '')
}

function expandToWord(value, start, end) {
  if (!value || start !== end) return { start, end }

  let s = start
  let e = end

  // If cursor is at the end of a word, expand from the left char.
  if (!isWordChar(value[s]) && isWordChar(value[s - 1])) {
    s -= 1
    e -= 1
  }

  if (!isWordChar(value[s])) return { start, end }

  while (s > 0 && isWordChar(value[s - 1])) s -= 1
  while (e < value.length && isWordChar(value[e])) e += 1

  return { start: s, end: e }
}

/**
 * Wrap the currently selected text in a textarea with the given marker.
 * If no text is selected, inserts the markers and places the cursor inside.
 */
export function applyFormat(textareaEl, marker, value, setValue) {
  if (!textareaEl) return
  const sourceValue = typeof textareaEl.value === 'string' ? textareaEl.value : (value || '')
  const rawStart = textareaEl.selectionStart ?? 0
  const rawEnd = textareaEl.selectionEnd ?? rawStart
  const { start, end } = expandToWord(sourceValue, rawStart, rawEnd)

  const selected = sourceValue.substring(start, end)
  const before = sourceValue.substring(0, start)
  const after = sourceValue.substring(end)

  const newValue = `${before}${marker}${selected}${marker}${after}`
  setValue(newValue)

  // Restore cursor / selection after React re-renders
  requestAnimationFrame(() => {
    textareaEl.focus()
    if (selected.length > 0) {
      textareaEl.setSelectionRange(start + marker.length, end + marker.length)
      return
    }
    textareaEl.setSelectionRange(start + marker.length, start + marker.length)
  })
}

/**
 * Parse inline formatting markers into React elements.
 * Handles: **bold**, *italic*, __underline__
 * Returns an array of strings and React elements.
 */
function parseInline(text) {
  // Order matters: match ** before * to avoid double-match
  const regex = /(\*\*[^*\n]+?\*\*|__[^_\n]+?__|(?<!\*)\*(?!\*)[^*\n]+?(?<!\*)\*(?!\*))/g
  const parts = []
  let lastIndex = 0
  let key = 0
  let match

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index))
    }
    const m = match[0]
    if (m.startsWith('**')) {
      parts.push(React.createElement('strong', { key: key++ }, m.slice(2, -2)))
    } else if (m.startsWith('__')) {
      parts.push(React.createElement('u', { key: key++ }, m.slice(2, -2)))
    } else {
      parts.push(React.createElement('em', { key: key++ }, m.slice(1, -1)))
    }
    lastIndex = match.index + m.length
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex))
  }
  return parts
}

/**
 * Render a block of text with inline formatting into an array of <p> / <br> elements.
 */
export function renderFormattedText(text) {
  if (!text) return null
  return text.split('\n').map((line, i) => {
    if (!line.trim()) return React.createElement('br', { key: i })
    return React.createElement('p', { key: i }, parseInline(line))
  })
}
